/**
 * Created: 2026-02-14
 * Initial commit: Add site admin QR code feature with scene length validation (d2c7cd3)
 *
 * Dialog component for displaying action QR codes for WeChat scanning.
 * Supports cross-device authentication and action flows (login, join, addadmin).
 */

import { gql } from '@apollo/client'
import { useSubscription } from '@apollo/client/react'
import { Box, Dialog, DialogContent, Typography } from '@mui/material'
import type * as React from 'react'
import { useEffect, useState } from 'react'
import type { UserBasic } from '../gql/graphql'
import { ActionQrScannedDocument } from '../gql/graphql'

gql`
  subscription ActionQrScanned($qrid: ID!) {
    actionQrScanned(qrid: $qrid) {
      qrid
      status
      user {
        id
        openid
        name
        avatar
      }
    }
  }
`

interface ActionQrDialogProps {
  open: boolean
  onClose?: () => void
  action: string
  actionId?: string
  title: string
  onScanned?: (user: Pick<UserBasic, 'id' | 'openid' | 'name' | 'avatar'>) => void
  extraInfo?: string
}

/**
 * A dialog that displays an action QR code for users to scan with WeChat
 */
const ActionQrDialog: React.FC<ActionQrDialogProps> = ({
  open,
  onClose,
  action,
  actionId,
  title,
  onScanned,
  extraInfo = '',
}) => {
  const [qrUrl, setQrUrl] = useState<string>()
  const [qrid, setQrid] = useState<string>()

  const { data } = useSubscription(ActionQrScannedDocument, {
    variables: { qrid: qrid as string },
    skip: !qrid || !open,
  })

  useEffect(() => {
    if (data?.actionQrScanned && onScanned) {
      const { user } = data.actionQrScanned
      if (user?.openid) {
        onScanned(user)
      }
    }
  }, [data, onScanned])

  useEffect(() => {
    if (!open) {
      setQrid(undefined)
      return
    }

    let objectUrl: string

    const fetchImage = async () => {
      const authToken = localStorage.getItem('authToken')
      const headers: HeadersInit = {}

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`
      }

      const params = new URLSearchParams({ action })
      if (actionId) params.set('action_id', actionId)
      if (extraInfo) params.set('extra_info', extraInfo)

      const response = await fetch(`/api/actionqr?${params}`, { headers })
      objectUrl = URL.createObjectURL(await response.blob())
      const qrId = response.headers.get('Action-QR-Id')

      setQrUrl(objectUrl)
      if (qrId) {
        setQrid(qrId)
      }
    }

    fetchImage()

    // Cleanup function: Revoke the Blob URL when the component unmounts
    // or when the dialog closes to prevent memory leaks.
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
        setQrUrl(undefined)
      }
    }
  }, [open, action, actionId, extraInfo])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDialog-paperWidthSm': { width: 350 },
        marginLeft: 'auto',
        marginRight: 'auto',
        mt: 6,
      }}
    >
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" component="div" sx={{ textAlign: 'center', mb: 2 }}>
            {title}
          </Typography>

          {qrUrl && (
            <Box
              component="img"
              src={qrUrl}
              alt="action qr code"
              data-testid="qr-code-image"
              sx={{
                width: 200,
                height: 200,
                borderRadius: 2,
                boxShadow: 3,
                cursor: 'pointer',
              }}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ActionQrDialog
